import { IServerMessage } from "../shared/IServerMessage";
import { IClientMessage } from "../shared/IClientMessage";
import { appState, getScm } from ".";
import { ClientOpCode } from "../shared/OpCodes";
import { createLogger } from "../shared/Logger.ts";
import { DisconnectedScene } from "../scenes/DisconnectedScene.ts";
const logger = createLogger("SocketClient");

/**
 * Client class for establishing network connection with the
 * server and sending/receiving game events via web sockets.
 */
export class SocketClient {
    private isConnected: boolean = false;
    private socket: WebSocket | null = null;
    private uid: number = -1;
    private isInitialized = false;


    public disconnect(){
        if(this.socket){
            this.socket.close(3333, "Client requested disconnect");
            this.socket = null;
        }
    }

    /**
     * Connects to a websocket server using the provided auth token and player id.
     * @param token auth token obtained during login
     * @returns bool indicating success
     */
    public async connect(token: string): Promise<boolean> {
        if (this.isConnected) return true;

        const url = `${import.meta.env.VITE_WEB_SOCKET_URL}?idToken=${token}`;
        logger.log(`connecting to: ${url}`);

        return new Promise((resolve, reject) => {
            this.socket = new WebSocket(url);
            this.socket.onopen = () => {
                logger.log("Connected to WebSocket server");
                this.isConnected = true;
                resolve(true);
            };
            this.socket.onerror = (error) => {
                logger.error("WebSocket error:", error);
                this.isConnected = false;
                reject(error);
            };
            this.socket.onmessage = this.handleServerMessage.bind(this);
            this.socket.onclose = (e: CloseEvent) => {
                this.isConnected = false;
                this.isInitialized = false;

                logger.warn(`SocketClient::onclose Disconnected from server! Code: ${e.code}, Reason: ${e.reason}`);

                if (e.code === 4003) {
                    logger.warn("Unauthorized: Invalid idToken");
                } else {
                    logger.warn(`Disconnected from server: ${e.reason || "Unknown reason"}`);
                }

                if(e.code != 3333) {    //  do not display disconnected scene if requested by client
                    const scm = getScm();
                    if (!scm.HasScene("DisconnectedScene")) {
                        scm.AddScene(new DisconnectedScene());
                    }
                    const discoScene = scm.GetScene("DisconnectedScene");
                    scm.ActivateScene(discoScene);
                }
            }
        });
    }

    /**
       * Creates a ClientMessage and sends it to the server.
       * @param commandOpCode the command opcode 
       * @param data the command payload
       */
    public sendMessage(commandOpCode: ClientOpCode, data?: string) {
        if (!this.isConnected) {
            logger.error('Sending data while not connected to websocket server!');
            return;
        }
        const msg: IClientMessage = { Code: commandOpCode, Data: data, UserId: this.uid };
        logger.log('C2S sending:', ClientOpCode[commandOpCode], msg);
        this.socket!.send(JSON.stringify(msg));
    }

    private decoder = new TextDecoder();
    private handleServerMessage(event: MessageEvent) {
        let msg: IServerMessage;
        if (typeof event.data === "string") {
            msg = JSON.parse(event.data) as IServerMessage;
        } else if (event.data instanceof ArrayBuffer) {
            // Process the binary data as needed
            const binaryData = new Uint8Array(event.data);
            logger.log("Received binary data:", binaryData);
            // Assuming the binary data can be converted to a string
            const strData = this.decoder.decode(binaryData);
            msg = JSON.parse(strData) as IServerMessage;
        }
        else {
            logger.error("Unknown message type:", event.data);
            return;
        }

        //  although technically not needed, the first server message 
        //  received must contain the player id: {"playerId": number}
        if (!this.isInitialized && msg.Data) {  //  Data might be undefined if the first server message is error
            logger.log('Initial server message:', msg);
            const obj = JSON.parse(msg.Data);
            this.uid = obj.playerId;
            this.isInitialized = true;
        }

        //console.log(msg.Data);
        const prev = lastByOpCode.get(msg.Code);
        if (prev === msg.Data) {
            logger.log('Ignoring duplicate message:', msg);
            return;       // identical JSON → ignore
        }
        lastByOpCode.set(msg.Code, msg.Data);

        appState.dispatch(msg);
    }
}
//  Map to keep track of last message by OpCode to avoid processing duplicates
const lastByOpCode = new Map<number, string>();